<template>
  <div :class="cn('tw:flex tw:flex-col tw:gap-1.5', $attrs.class as any)" :style="$attrs.style as any">
    <label
      :class="
        cn(
          'tw:inline-flex! tw:cursor-pointer tw:select-none tw:items-center tw:gap-2.5',
          disabled && 'tw:cursor-not-allowed'
        )
      ">
      <!-- the native input stays in the tree but off-screen: it carries the checked state, the label
           association, keyboard operation and Space to toggle, and a screen reader announces it
           correctly. The box below carries the visual, exactly as the design system renders it -->
      <span
        :class="
          cn(
            BOX_BASE,
            filled ? BOX_ON : BOX_OFF,
            invalid && BOX_INVALID,
            disabled && (filled ? BOX_DISABLED_ON : BOX_DISABLED_OFF)
          )
        ">
        <input
          ref="inputRef"
          v-bind="forwardedAttrs"
          type="checkbox"
          class="tw:sr-only"
          :checked="modelValue"
          :disabled="disabled"
          :aria-invalid="invalid || undefined"
          :aria-describedby="messageId"
          @change="onChange" />
        <IconMinus v-if="indeterminate" :size="14" :stroke-width="2.5" class="tw:pointer-events-none tw:text-white" />
        <IconCheck v-else-if="modelValue" :size="14" :stroke-width="2.5" class="tw:pointer-events-none tw:text-white" />
      </span>

      <span
        v-if="label != null || $slots.default"
        :class="[
          'tw:text-14 tw:font-normal tw:[text-wrap:pretty]',
          disabled ? 'tw:text-[rgb(16_24_40/0.3)]' : 'tw:text-gray-900',
        ]">
        <!-- slot as well as prop so labels can contain links -->
        <slot>{{ label }}</slot>
      </span>
    </label>

    <div v-if="errorList.length > 0" :id="messageId" class="tw:text-13 tw:font-normal tw:text-red-600">
      <span v-if="errorList.length === 1">{{ errorList[0] }}</span>
      <ul v-else class="tw:list-disc tw:pl-4">
        <li v-for="error in errorList" :key="error">{{ error }}</li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
// mirrors the LEAD Design System <Checkbox>, with class strings taken from its Checkbox.tsx;
// `errors` is the one addition beyond the DS source
import { computed, ref, useAttrs, useId, watchEffect } from 'vue';
import { IconCheck, IconMinus } from '@tabler/icons-vue';
import { cn } from '@/lib/cn';

defineOptions({ inheritAttrs: false });

interface Props {
  modelValue?: boolean;
  /** plain-text label; use the default slot instead when the label contains links */
  label?: string;
  disabled?: boolean;
  /** purely visual tri-state flag, independent of the checked model */
  indeterminate?: boolean;
  errors?: string[] | string | null;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  label: undefined,
  disabled: false,
  indeterminate: false,
  errors: null,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  change: [event: Event];
}>();

const attrs = useAttrs();
const generatedId = useId();
const inputRef = ref<HTMLInputElement | null>(null);

/* --------------------------------- COMPUTED ------------------------------- */

const forwardedAttrs = computed(() => {
  const { class: _c, style: _s, id: _i, ...rest } = attrs as any;
  return rest;
});

const errorList = computed<string[]>(() => {
  if (props.errors == null) return [];
  return Array.isArray(props.errors) ? props.errors.filter(Boolean) : [props.errors].filter(Boolean);
});
const invalid = computed(() => errorList.value.length > 0);
const messageId = computed(() => (errorList.value.length > 0 ? `lead-checkbox-${generatedId}-message` : undefined));

// a mixed box counts as filled
const filled = computed(() => props.modelValue || props.indeterminate);

/* ------------------------ DS CLASS STRINGS (verbatim) --------------------- */

const BOX_BASE = [
  'tw:relative tw:inline-flex tw:size-[18px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-sm tw:border tw:border-solid',
  'tw:transition-[background-color,border-color,box-shadow] tw:duration-150 tw:ease-in-out',
  'tw:has-focus-visible:outline-none tw:has-focus-visible:shadow-[0_0_0_4px_rgb(0_122_255/0.1)]',
].join(' ');
const BOX_OFF = 'tw:border-gray-300 tw:bg-white tw:shadow-xs tw:has-enabled:hover:border-primary';
const BOX_ON =
  'tw:border-primary tw:bg-primary tw:has-enabled:hover:border-primary-darken tw:has-enabled:hover:bg-primary-darken';
const BOX_INVALID =
  'tw:border-red-600 tw:has-enabled:hover:border-red-600 tw:has-focus-visible:shadow-[0_0_0_4px_rgb(231_0_11/0.1)]';
const BOX_DISABLED_ON = 'tw:border-primary/40 tw:bg-primary/40 tw:shadow-none';
const BOX_DISABLED_OFF = 'tw:border-gray-200 tw:bg-gray-100 tw:shadow-none';

/* --------------------------------- METHODS -------------------------------- */

const onChange = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;
  emit('update:modelValue', checked);
  emit('change', event);
};

/* --------------------------------- WATCHERS -------------------------------- */
// indeterminate has no HTML attribute equivalent, it can only be set as a DOM property
watchEffect(
  () => {
    if (inputRef.value) inputRef.value.indeterminate = props.indeterminate;
  },
  { flush: 'post' }
);
</script>
